import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ApiError, apiFetch } from '@/lib/api/client';
import { useToast } from '@/components/ui/toast';
import { DEALS_QUERY_KEY } from '@/features/pipeline/useDeals';
import { parseDecimalPtBr } from '@/lib/domain/format';
import type { ApiInvestmentResponse } from './types';
import { INVESTMENTS_QUERY_KEY } from './useInvestments';

export interface HandoffForm {
  codename: string;
  /** Valor digitado do MOIC esperado (ex.: "2,1"); vazio = não informado. */
  moicExpected: string;
  /** Valor digitado da TIR esperada em % (ex.: "22"); vazio = não informado. */
  irrExpected: string;
  fundId: number | null;
  leadUserId: string;
  teamUserIds: string[];
  /** Parte discricionária do cheque digitada (ex.: "180000000"); vazio = não informado. */
  checkDiscretionary: string;
  /** Parte de coinvestimento do cheque digitada; vazio = não informado. */
  checkCoinvestment: string;
}

export interface HandoffBody {
  codename: string;
  fundId: number;
  leadUserId: string;
  teamUserIds: string[];
  moicExpected: number | null;
  irrExpected: number | null;
  checkDiscretionaryBRL: number | null;
  checkCoinvestmentBRL: number | null;
}

/** Achado F15: reexporta a fonte única (lib/domain/format.ts) sem tocar quem já importa daqui. */
export const parseDecimal = parseDecimalPtBr;

/**
 * Monta o corpo do handoff a partir do formulário (função pura, testável). MOIC vai como múltiplo
 * (2,1 → 2.1); TIR é digitada em % e convertida para fração (22 → 0.22, como o backend espera).
 */
export function buildHandoffBody(form: HandoffForm): HandoffBody {
  const moic = parseDecimal(form.moicExpected);
  const irrPct = parseDecimal(form.irrExpected);
  return {
    codename: form.codename.trim(),
    fundId: form.fundId ?? 0,
    leadUserId: form.leadUserId,
    teamUserIds: form.teamUserIds,
    moicExpected: moic,
    irrExpected: irrPct == null ? null : irrPct / 100,
    checkDiscretionaryBRL: parseDecimal(form.checkDiscretionary),
    checkCoinvestmentBRL: parseDecimal(form.checkCoinvestment),
  };
}

/**
 * Handoff de um Deal para o Portfólio: POST /api/deals/{id}/handoff com If-Match (ETag do Deal).
 * Cria o Investment e fecha o Deal (ClosedToPortfolio). Em sucesso, invalida os deals (o card sai
 * do board) e os investimentos (aparece no Portfólio). 412 → o Deal mudou → aviso + refetch.
 */
export function useHandoffDeal() {
  const qc = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ id, etag, body }: { id: string; etag: string; body: HandoffBody }) =>
      apiFetch<ApiInvestmentResponse>(`/api/deals/${id}/handoff`, {
        method: 'POST',
        headers: { 'If-Match': `"${etag}"` },
        body,
      }),
    onSuccess: (resp) => {
      void qc.invalidateQueries({ queryKey: DEALS_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: INVESTMENTS_QUERY_KEY });
      toast(`Movido para o Portfólio: ${resp.codename}.`);
    },
    onError: (err) => {
      if ((err as ApiError).status === 412) {
        toast('Esse deal foi alterado por outra pessoa. Atualizei a lista.', 'warn');
        void qc.invalidateQueries({ queryKey: DEALS_QUERY_KEY });
      } else {
        toast('Não foi possível mover o deal para o Portfólio. Confira os campos e tente de novo.', 'danger');
      }
    },
  });
}
